import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach admin token from localStorage on each request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("oaa_admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- Public ----------
export const getAccidentTypes = () => api.get("/accident-types").then((r) => r.data);
export const getAccidentType = (slug) => api.get(`/accident-types/${slug}`).then((r) => r.data);
export const getCities = () => api.get("/cities").then((r) => r.data);
export const getCity = (slug) => api.get(`/cities/${slug}`).then((r) => r.data);
export const getBlogPosts = (category) =>
  api.get("/blog/posts", { params: { limit: 500, ...(category ? { category } : {}) } }).then((r) => r.data);
export const getBlogPost = (slug) => api.get(`/blog/posts/${slug}`).then((r) => r.data);
export const getBlogCategories = () => api.get("/blog/categories").then((r) => r.data);
export const getFAQs = () => api.get("/faqs").then((r) => r.data);
export const getTestimonials = () => api.get("/testimonials").then((r) => r.data);
export const getStats = () => api.get("/stats").then((r) => r.data);
export const submitLead = (data) => api.post("/leads", data).then((r) => r.data);

// ---------- Auth ----------
export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const adminLogout = () => api.post("/auth/logout").then((r) => r.data);

// ---------- Admin CMS ----------
export const adminListLeads = () => api.get("/admin/leads").then((r) => r.data);
export const adminUpdateLead = (id, data) => api.patch(`/admin/leads/${id}`, data).then((r) => r.data);
export const adminDeleteLead = (id) => api.delete(`/admin/leads/${id}`).then((r) => r.data);

export const adminList = (resource) => api.get(`/admin/${resource}`).then((r) => r.data);
export const adminCreate = (resource, data) =>
  api.post(`/admin-create/${resource}`, data).then((r) => r.data);
export const adminUpdate = (resource, id, data) =>
  api.put(`/admin-update/${resource}/${id}`, data).then((r) => r.data);
export const adminDelete = (resource, id) =>
  api.delete(`/admin-delete/${resource}/${id}`).then((r) => r.data);
export const adminDuplicate = (resource, id, overrides) =>
  api.post(`/admin-duplicate/${resource}/${id}`, overrides).then((r) => r.data);

export const aiGenerateBlog = (topic, keywords, category) =>
  api.post("/admin/ai/generate-blog", { topic, keywords, category }).then((r) => r.data);

// ---------- Content Strategy ----------
export const adminContentStats = () =>
  api.get("/admin/content-stats").then((r) => r.data);

export const adminStartManualFromIdea = (ideaId) =>
  api.post(`/admin/topic-ideas/${ideaId}/start-manual`).then((r) => r.data);

export const adminGenerateAIDraftFromIdea = (ideaId) =>
  api.post(`/admin/topic-ideas/${ideaId}/generate-ai-draft`).then((r) => r.data);

// ---------- Phase 2: Editorial Pipeline ----------
export const adminBulkGenerate = (ideaIds) =>
  api.post("/admin/bulk-generate-drafts", { idea_ids: ideaIds }).then((r) => r.data);

export const adminBulkJob = (jobId) =>
  api.get(`/admin/bulk-jobs/${jobId}`).then((r) => r.data);

export const adminAutoLink = (postId) =>
  api.post(`/admin/blog/${postId}/auto-link`).then((r) => r.data);

export const adminGenerateSeoMeta = (postId) =>
  api.post(`/admin/blog/${postId}/generate-seo-meta`).then((r) => r.data);

export const adminGenerateFaqs = (postId) =>
  api.post(`/admin/blog/${postId}/generate-faqs`).then((r) => r.data);

export const adminCalendar = () =>
  api.get("/admin/calendar").then((r) => r.data);

// ---------- Phase 3: Google Search Console ----------
export const gscGetSettings = () =>
  api.get("/admin/settings/gsc").then((r) => r.data);

export const gscSaveSettings = (payload) =>
  api.post("/admin/settings/gsc", payload).then((r) => r.data);

export const gscAnalytics = (dimension, days = 28, rowLimit = 100) =>
  api.get(`/admin/gsc/analytics`, { params: { dimension, days, row_limit: rowLimit } }).then((r) => r.data);

export const gscListSitemaps = () =>
  api.get("/admin/gsc/sitemaps").then((r) => r.data);

export const gscSubmitSitemap = (sitemap_url) =>
  api.post("/admin/gsc/sitemaps/submit", { sitemap_url }).then((r) => r.data);

export const gscRequestIndexing = (url) =>
  api.post("/admin/gsc/indexing/notify", { url }).then((r) => r.data);

export const gscIndexingLog = () =>
  api.get("/admin/gsc/indexing/log").then((r) => r.data);

export const gscIndexingRetry = (log_id) =>
  api.post("/admin/gsc/indexing/retry", { log_id }).then((r) => r.data);

export const getSiteConfig = () =>
  api.get("/site-config").then((r) => r.data);

// Build a downloadable URL with the admin token attached (export endpoints).
export const adminExportUrl = (path) => {
  const token = localStorage.getItem("oaa_admin_token");
  return { url: `${API}/admin/export${path}`, token };
};

// Download an authenticated export to the browser as a file.
export const downloadAdminExport = async (path, filename) => {
  const token = localStorage.getItem("oaa_admin_token");
  const r = await fetch(`${API}/admin/export${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error("Export failed");
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const uploadImage = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const token = localStorage.getItem("oaa_admin_token");
  return axios
    .post(`${API}/admin/upload-image`, fd, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((r) => r.data);
};

export default api;
